
import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { TabsContent } from "@/components/ui/tabs";
import { AddClientInteractionDialog } from './AddClientInteractionDialog';
import { TimelineHeader } from './timeline/TimelineHeader';
import { TimelineFilters } from './timeline/TimelineFilters';
import { InteractionItem } from './timeline/InteractionItem';
import { EmptyTimeline } from './timeline/EmptyTimeline';

interface ClientInteraction {
  id: string;
  type: 'email' | 'call' | 'meeting' | 'note' | 'document';
  title: string;
  content?: string;
  date: Date;
  user: string;
  status?: 'completed' | 'scheduled' | 'cancelled';
  priority?: 'high' | 'medium' | 'low';
}

interface ClientCommunicationTimelineProps {
  clientId: string;
  clientName: string;
}

// Mock data - in real implementation, this would come from the database
const mockInteractions: ClientInteraction[] = [
  {
    id: '1',
    type: 'email',
    title: 'Initial project discussion',
    content: 'Discussed requirements for senior React developer position.',
    date: new Date('2024-01-15T09:30:00'),
    user: 'John Smith',
    status: 'completed',
    priority: 'high'
  },
  {
    id: '2',
    type: 'call',
    title: 'Follow-up on candidate shortlist',
    content: 'Reviewed top 3 candidates for the React position.',
    date: new Date('2024-01-20T14:00:00'),
    user: 'Sarah Johnson',
    status: 'completed',
    priority: 'medium'
  },
  {
    id: '3',
    type: 'meeting',
    title: 'Quarterly review meeting',
    content: 'Upcoming meeting to review placement success and future needs.',
    date: new Date('2024-02-01T10:00:00'),
    user: 'John Smith',
    status: 'scheduled',
    priority: 'high'
  }
];

export const ClientCommunicationTimeline: React.FC<ClientCommunicationTimelineProps> = ({ 
  clientId, 
  clientName 
}) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [interactions] = useState<ClientInteraction[]>(mockInteractions);

  const filterInteractions = (interactions: ClientInteraction[], filter: string) => {
    if (filter === 'all') return interactions;
    return interactions.filter(interaction => interaction.type === filter);
  };

  const filteredInteractions = filterInteractions(interactions, activeFilter);

  const interactionCounts = {
    all: interactions.length,
    email: interactions.filter(i => i.type === 'email').length,
    call: interactions.filter(i => i.type === 'call').length,
    meeting: interactions.filter(i => i.type === 'meeting').length,
    note: interactions.filter(i => i.type === 'note').length,
  };

  return (
    <Card>
      <CardHeader>
        <TimelineHeader onAddInteraction={() => setIsAddDialogOpen(true)} />
      </CardHeader>
      <CardContent>
        <TimelineFilters 
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          interactionCounts={interactionCounts}
        />
        
        <TabsContent value={activeFilter} className="mt-6">
          {filteredInteractions.length === 0 ? (
            <EmptyTimeline 
              activeFilter={activeFilter}
              onAddInteraction={() => setIsAddDialogOpen(true)}
            />
          ) : (
            <div className="space-y-4">
              {filteredInteractions
                .sort((a, b) => b.date.getTime() - a.date.getTime())
                .map((interaction) => (
                  <InteractionItem key={interaction.id} interaction={interaction} />
                ))}
            </div>
          )}
        </TabsContent>
      </CardContent>
      
      <AddClientInteractionDialog
        clientId={clientId}
        clientName={clientName}
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onInteractionAdded={() => {
          console.log('Interaction added, refreshing timeline...');
        }}
      />
    </Card>
  );
};
